import React, { useContext, useRef, useState } from 'react'
import { View } from 'react-native'
import { useNavigation, useRoute } from '@react-navigation/native'
import { StackNavigationProp } from '@react-navigation/stack'
import { useSelector } from 'react-redux'
import { StyleService, useStyleSheet } from '@src/style/service'
import { Button, Text } from '@src/components/base'
import { NavigationContainer } from '@src/screens/Common/containers'
import { ReactNativeFile } from '@src/utils/image'
import { AppRouteProp, AppStackParamList } from '@src/navigation/types'
import { useDirectUpload } from '@src/hooks/useDirectUpload'
import { UserFileTag } from '@src/types'
import { NavigatorContext, useDispatchAsync, useSnack } from '@src/utils'
import { labResultsFileUploadLimitMBSelector } from '@src/selectors/app'
import { useSecureScreen } from '@src/hooks/useSecureScreen'
import { LabUpload } from '../components/LabUpload'

export const UploadLabResultsScreen = () => {
  useSecureScreen()

  const styles = useStyleSheet(themedStyles)
  const navigation = useNavigation<StackNavigationProp<AppStackParamList>>()
  const dispatchAsync = useDispatchAsync()
  const uploadFile = useDirectUpload()
  const showSnack = useSnack()
  const { params } = useRoute<AppRouteProp<'UploadLabResults'>>()
  const initialFile = params.file
  const labResultsUploadLimitMB = useSelector(labResultsFileUploadLimitMBSelector)

  const [file, setFile] = useState<ReactNativeFile | undefined>(initialFile)
  const { enableLoader, disableLoader } = useContext(NavigatorContext)
  const loadingRef = useRef(false)

  const fileTooLarge = (fileSizeInBytes: number | undefined) => {
    if (!fileSizeInBytes) {
      return false
    }

    const fileSizeMB = fileSizeInBytes / 1024 / 1024 // convert bytes to MB

    return fileSizeMB > labResultsUploadLimitMB
  }

  const onUploadButtonPress = async () => {
    if (file) {
      if (fileTooLarge(file.sizeInBytes)) {
        showSnack(
          'File size limit exceeded',
          `Lab results file must be less than ${labResultsUploadLimitMB} MB.`,
          'error',
        )
        return
      }

      loadingRef.current = true
      enableLoader()

      const labUpload = await uploadFile({
        uri: file.uri,
        fileTag: UserFileTag.LabWorkResults,
        filename: file.name,
        contentType: file.type,
      })

      if (labUpload?.id) {
        try {
          await dispatchAsync({
            type: 'labResults/createLabResultFromUpload',
            payload: {
              fileId: labUpload.id,
            },
          })
          navigation.replace('UploadLabResultsSuccess')
        } catch {
          showSnack(
            'Error occurred',
            'Failed to submit lab results. Please try again in a few minutes.',
            'error',
          )
          navigation.replace('AddLabResults')
        }
      } else {
        showSnack(
          'Error occurred',
          'Failed to upload file, please try again in a few minutes.',
          'error',
        )
      }

      disableLoader()
      loadingRef.current = false
    }
  }

  return (
    <NavigationContainer title="Upload Lab Results">
      <View style={styles.container}>
        <Text type="regular" style={styles.descriptionText}>
          Upload a photo, PDF, or screenshot of your lab results to import your health metrics. Only
          one file per upload is permitted at this time.
        </Text>
        <Text type="large" bold>
          Add Lab Results
        </Text>

        <View style={styles.uploadSection}>
          <LabUpload onFileChange={setFile} file={file} />
        </View>
        <View style={styles.buttonsContainer}>
          <Button
            type="primary"
            style={styles.uploadButton}
            disabled={!file || loadingRef.current}
            size="block"
            accessibilityLabel="Upload"
            onPress={onUploadButtonPress}
          >
            Upload
          </Button>
          {file && (
            <Button
              type="secondary"
              style={styles.uploadButton}
              size="block"
              accessibilityLabel="Remove upload"
              onPress={() => setFile(undefined)}
            >
              Remove
            </Button>
          )}
        </View>
      </View>
    </NavigationContainer>
  )
}

const themedStyles = StyleService.create({
  container: {
    flex: 1,
    padding: 16,
    backgroundColor: 'theme.background',
  },
  uploadSection: {
    marginTop: 16,
    alignSelf: 'stretch',
  },
  descriptionText: {
    marginTop: 16,
    marginBottom: 32,
  },
  uploadButton: {
    flex: 1,
  },
  buttonsContainer: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 24,
  },
})
